import {Component, DestroyRef, inject, Inject, OnInit} from '@angular/core';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FormControl, FormGroup, Validators} from '@angular/forms';
import {ItemsFacadeService} from '@emm/management/services/items-facade.service';
import {ItemAttributeType} from '@emm/core/models/enums';
import {formatDateToMidnight} from '@emm/core/util/menu-date.util';
import {BaseItemRequestPayload} from '@emm/common-items/models/item.model';
import {ItemTypeService} from '@emm/core/services/item-type.service';
import {BehaviorSubject} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {CommonItemAttribute} from '@emm/common-items/models/commonItemAttribute.model';
import {UserService} from '@emm/core/services/user.service';
import {NotificationService} from '@emm/shared/services/notification.service';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {formatFieldName} from '@emm/core/util/string-format.util';
import {ItemType} from '@emm/core/models/item-type.model';

@Component({
  selector: 'emm-create-common-item-dialog',
  templateUrl: './create-common-item-dialog.component.html',
  styleUrl: './create-common-item-dialog.component.scss'
})
export class CreateCommonItemDialogComponent implements OnInit {
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    private readonly DEFAULT_COUNTRY = 'US';
    private readonly DEFAULT_LANGUAGE = 'en';
    private readonly DEFAULT_ATTRIBUTE_GROUP = 'CORE';
    private readonly PRIORITY_ORDER = ['tag', 'name'];

    effectiveDate: Date | null = new Date(Date.now() + 24 * 60 * 60 * 1000);
    classForm = new FormGroup ({
        class: new FormControl('ITEM'),
        effectiveDate: new FormControl(this.effectiveDate),
    });
    form: FormGroup;
    formReady = false;
    globalAttributes;
    classAttributes: CommonItemAttribute[] = [
        {
            attributeGroup: this.DEFAULT_ATTRIBUTE_GROUP,
            countryCode: this.DEFAULT_COUNTRY,
            language: this.DEFAULT_LANGUAGE,
            name: 'Type',
            required: true,
            type: ItemAttributeType.Select,
            jsonProperty: 'type',
            tag: 'TYPE',
            value: null,
        }
    ];
    sortedAttributes;
    allTypes$ = new BehaviorSubject<ItemType[]>([]);
    user;

    constructor(
        private dialogRef: MatDialogRef<CreateCommonItemDialogComponent>,
        @Inject(MAT_DIALOG_DATA) private data: {},
        private itemsFacade: ItemsFacadeService,
        private typeService: ItemTypeService,
        private userService: UserService,
        private notificationService: NotificationService,
    ) {}

    ngOnInit() {
        this.userService.getUser().subscribe(user => {
            this.user = user;
            if (!this.user) {
                this.notificationService.error('Failed to load user')
            }
        });

        this.typeService.getAllItemTypes().pipe(
            tap(allTypes => {
                this.allTypes$.next(allTypes);
            }),
            switchMap(() => this.itemsFacade.attributes$),
            tap(globalAttributes => {
                globalAttributes.push({
                    attributeGroup: this.DEFAULT_ATTRIBUTE_GROUP,
                    dateUpdated: formatDateToMidnight(new Date().toISOString()),
                    defaultValue: undefined,
                    description: '',
                    itemTypeScope: [],
                    locked: false,
                    name: 'Tag',
                    options: undefined,
                    regionScope: undefined,
                    required: true,
                    scope: undefined,
                    type: ItemAttributeType.Text,
                    updatedBy: '',
                    validationExpression: '',
                    jsonProperty: 'tag',
                    tag: 'TAG'
                });

                globalAttributes.forEach(attr => {
                    if (attr.type === ItemAttributeType.Boolean) {
                        if (attr.defaultValue === 'false') {
                            attr.defaultValue = false;
                        } else if (attr.defaultValue === 'true') {
                            attr.defaultValue = true;
                        }

                        if (attr.jsonProperty === 'disabled') {
                            attr.defaultValue = true;
                        }

                        if (Array.isArray(attr.options)) {
                            attr.options = attr.options.map(opt => {
                                if (typeof opt === 'object' && opt !== null) {
                                    const strValue = String(opt.value || opt.label || opt).toLowerCase();
                                    const boolValue = strValue === 'true';
                                    return {
                                        label: strValue.charAt(0).toUpperCase() + strValue.slice(1),
                                        value: boolValue
                                    };
                                } else {
                                    const strValue = String(opt).toLowerCase();
                                    const boolValue = strValue === 'true';
                                    return {
                                        label: strValue.charAt(0).toUpperCase() + strValue.slice(1),
                                        value: boolValue
                                    };
                                }
                            });
                        }
                    }
                });
                this.globalAttributes = globalAttributes;
                // TODO: Add logic that only allows relevant attributes to be added to the form.
                //  This should be based on the class the user selects.
                this.buildForm();
            })
        ).subscribe();

        this.valueChangeHandler();
    }

    sortFormAttributes<T extends Record<string, any>>(attributes: T[]): T[] {
        const getName = (item: T): string => item.name || item.jsonProperty || '';
        const isRequired = (item: T): boolean => item.required;
        const lower = (v: string) => v?.toLowerCase();

        const sorted = attributes.sort((a, b) => {
            const aRequired = isRequired(a);
            const bRequired = isRequired(b);

            if (aRequired !== bRequired) {
                return aRequired ? -1 : 1;
            }

            const aName = getName(a).toLowerCase();
            const bName = getName(b).toLowerCase();
            return aName.localeCompare(bName);
        });

        const priority = this.PRIORITY_ORDER.flatMap(p =>
            sorted.filter(a => lower(getName(a)) === p || lower(a.jsonProperty) === p)
        );

        if (priority.length) {
            const rest = sorted.filter(a =>
                !this.PRIORITY_ORDER.includes(lower(getName(a))) &&
                !this.PRIORITY_ORDER.includes(lower(a.jsonProperty))
            );
            return [...priority, ...rest];
        }
        return sorted;
    }

    buildForm() {
        this.updateTypeOptions();

        this.sortedAttributes = this.formatAttributeNames(this.sortFormAttributes(this.globalAttributes));
        this.classAttributes = this.formatAttributeNames(this.classAttributes);

        const dynamicGroup = this.createFormControls();
        this.form = new FormGroup(dynamicGroup);
        this.formReady = true;
    }

    private updateTypeOptions(): void {
        const typeAttribute = this.classAttributes.find(attribute => attribute.tag === 'TYPE');
        const selectedClass = this.classForm.controls['class'].value;

        if (typeAttribute) {
            const allTypes = this.allTypes$.value;
            if (!allTypes || allTypes.length === 0) {
                typeAttribute.options = [];
            } else if (selectedClass) {
                typeAttribute.options = allTypes.filter(type => type.classTag === selectedClass);
            } else {
                typeAttribute.options = allTypes;
            }
        }
    }

    private formatAttributeNames(attributes: CommonItemAttribute[]): CommonItemAttribute[] {
        return attributes.map(attr => ({
            ...attr,
            name: formatFieldName(attr.name)
        }));
    }

    private createFormControls(): Record<string, FormControl> {
        const dynamicGroup: Record<string, FormControl> = {};

        // Add all class attributes (all required)
        this.classAttributes.forEach(attribute => {
            const validators = [Validators.required];
            if (attribute.type === ItemAttributeType.Text || attribute.type === ItemAttributeType.TextArea) {
                validators.push(this.noWhitespaceValidator);
            }

            // Add tag format validation
            if (attribute.tag === 'TAG' || attribute.jsonProperty === 'tag') {
                validators.push(this.tagFormatValidator);
            }

            dynamicGroup[attribute.jsonProperty] = new FormControl(
                {value: null, disabled: false},
                validators
            );
        });

        // Only add required global attributes to the form
        for (const attribute of this.sortedAttributes) {
            if (attribute.required) {
                const validators = [Validators.required];
                if (attribute.type === ItemAttributeType.Text || attribute.type === ItemAttributeType.TextArea) {
                    validators.push(this.noWhitespaceValidator);
                }

                // Add tag format validation
                if (attribute.tag === 'TAG' || attribute.jsonProperty === 'tag') {
                    validators.push(this.tagFormatValidator);
                }

                const defaultValue = attribute.defaultValue !== undefined ? attribute.defaultValue : null;
                const isFieldDisabled = attribute.jsonProperty === 'disabled';

                dynamicGroup[attribute.jsonProperty] = new FormControl(
                    {value: defaultValue, disabled: isFieldDisabled},
                    validators
                );
            }
        }

        return dynamicGroup;
    }

    close(): void {
        this.dialogRef.close();
    }

    save() {
        const formValues = this.form.getRawValue();
        let attributes: CommonItemAttribute[] = Object.entries(this.form.controls)
            .map(([jsonProperty, control]) => {
                const attr = this.findAttribute(jsonProperty);
                const actualValue = formValues[jsonProperty];

                return {
                    tag: attr?.tag,
                    jsonProperty,
                    value: (actualValue === 'True' || actualValue === 'False')
                        ? this.convertStringToBoolean(actualValue) : actualValue,
                    attributeGroup: attr?.attributeGroup,
                    type: attr?.type,
                    name: attr?.name,
                    countryCode: this.DEFAULT_COUNTRY,
                    language: this.DEFAULT_LANGUAGE
                } as CommonItemAttribute;
            });
        attributes = this.buildAttributesForCreate(this.form, formValues, attributes);

        const newItem: BaseItemRequestPayload = {
            tag: this.getAttributeValue(attributes, 'tag') as string,
            type: this.getAttributeValue(attributes, 'type') as string,
            isLocked: false,
            rmi: this.getAttributeValue(attributes, 'retailModifiedItemId') as number,
            name: this.getAttributeValue(attributes, 'name') as string,
            image: this.getAttributeValue(attributes, 'webImage') as string,
            description: this.getAttributeValue(attributes, 'description') as string,
            country: this.DEFAULT_COUNTRY,
            locationSetTag: 'CHAIN',
            attributes,
        };

        const requestedBy = `${this.user?.firstName} ${this.user?.lastName}` ?? this.user?.login ?? 'unknown';
        const itemClass = this.classForm.controls['class'].value;
        this.dialogRef.close({newItem, requestedBy, class: itemClass});
    }

    private findAttribute(jsonProperty: string): CommonItemAttribute | undefined {
        return this.sortedAttributes.find(a => a.jsonProperty === jsonProperty)
            ?? this.classAttributes.find(a => a.jsonProperty === jsonProperty);
    }

    private getAttributeValue(attributes: CommonItemAttribute[], jsonProperty: string): string | number | undefined {
        return attributes.find(attribute => attribute.jsonProperty === jsonProperty)?.value;
    }

    onDateChange(event: any) {
        this.effectiveDate = event?.value ?? null;
    }

    convertStringToBoolean(value: string): boolean {
        return value === 'True';
    }

    buildAttributesForCreate(
        form,
        formValues = {},
        attributes = [],
    ) {
        const hasFormGet = form && typeof form.get === 'function';

        const isDirtyOrRequired = (property) => {
            if (!property) {
                return false;
            }
            if (property === 'disabled') {
                return true;
            }
            if (hasFormGet) {
                const ctrl = form.get(property);
                return !!(ctrl && ctrl.dirty);
            }
            return false;
        };

        return attributes
            .filter(attr => attr.jsonProperty !== 'components')
            .filter(attr => isDirtyOrRequired(attr.jsonProperty))
            .map(attr => {
                const resolved = formValues?.[attr.jsonProperty] !== undefined
                    ? formValues[attr.jsonProperty]
                    : attr.value;

                const value = Array.isArray(resolved) ? JSON.stringify(resolved) : resolved;

                return {
                    ...attr,
                    value
                };
            });
    }

    hasAttribute(tag: string): boolean {
        return this.classAttributes.some(a => a.tag === tag);
    }

    getRequiredGlobalAttributes() {
        return this.sortedAttributes?.filter(attr => attr.required) ?? [];
    }

    compareBoolValues(v1: any, v2: any): boolean {
        const bool1 = v1 === true || v1 === 'true';
        const bool2 = v2 === true || v2 === 'true';
        return bool1 === bool2;
    }

    getFormValue(jsonProperty: string): any {
        const control = this.form?.get(jsonProperty);
        return control ? control.value : null;
    }

    getBoolDisplayText(jsonProperty: string): string {
        const value = this.getFormValue(jsonProperty);

        if (value === true) return 'True';
        if (value === false) return 'False';
        return value;
    }

    private createAttribute(
        tag: string,
        jsonProperty: string,
        type: string,
        name: string,
        required: boolean,
        options?: any[]
    ): CommonItemAttribute {
        return {
            tag,
            value: undefined,
            jsonProperty,
            attributeGroup: this.DEFAULT_ATTRIBUTE_GROUP,
            type,
            name,
            countryCode: this.DEFAULT_COUNTRY,
            language: this.DEFAULT_LANGUAGE,
            required,
            ...(options && { options })
        } as CommonItemAttribute;
    }

    buildModifierGroupAttributes(): CommonItemAttribute[] {
        return [
            this.createAttribute(
                'MODIFIER_TYPE_GROUPING',
                'modifierTypeGrouping',
                'SELECT',
                'Modifier Type Grouping',
                true,
                [
                    {
                        label: 'Bread',
                        value: 'Bread'
                    },
                    {
                        label: 'Dressings & Condiments',
                        value: 'Dressings & Condiments'
                    },
                    {
                        label: 'Extras',
                        value: 'Extras'
                    },
                    {
                        label: 'Ice Amount',
                        value: 'Ice Amount'
                    },
                    {
                        label: 'Toppings',
                        value: 'Toppings'
                    },
                    {
                        label: 'Sauces & Condiments',
                        value: 'Sauces & Condiments'
                    },
                    {
                        label: 'Salt',
                        value: 'Salt'
                    },
                    {
                        label: 'Condiments',
                        value: 'Condiments'
                    },
                    {
                        label: 'Protein',
                        value: 'Protein'
                    },
                    {
                        label: 'Cheese',
                        value: 'Cheese'
                    },
                    {
                        label: 'Sauces',
                        value: 'Sauces'
                    },
                    {
                        label: 'Dough',
                        value: 'Dough'
                    },
                    {
                        label: 'Veggies',
                        value: 'Veggies'
                    },
                    {
                        label: 'Sauces & Seasonings',
                        value: 'Sauces & Seasonings'
                    },
                    {
                        label: 'Base',
                        value: 'Base'
                    },
                    {
                        label: 'Dipping Sauces',
                        value: 'Dipping Sauces'
                    },
                    {
                        label: 'Seasonings',
                        value: 'Seasonings'
                    },
                    {
                        label: 'Dressings',
                        value: 'Dressings'
                    }
                ]
            ),
            this.createAttribute('MAXIMUM', 'maximum', 'NUM', 'Maximum', true),
            this.createAttribute('MINIMUM', 'minimum', 'NUM', 'Minimum', false),
        ];
    }
    buildModifierAttributes(): CommonItemAttribute[] {
        return [
            this.createAttribute(
                'MODIFIER_TYPE',
                'modifierType',
                'SELECT',
                'Modifier Type',
                true,
                [
                    {label: 'RECIPE', value: 'RECIPE'},
                    {label: 'DEFAULT', value: 'DEFAULT'},
                    {label: 'EXTRA', value: 'EXTRA'},
                    {label: 'NO_REFUND_RECIPE', value: 'NO_REFUND_RECIPE'}
                ]
            ),
            this.createAttribute('MAXIMUM', 'maximum', 'NUM', 'Maximum', false),
        ];
    }

    valueChangeHandler() {
        this.classForm.controls['class'].valueChanges.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((value) => {
            const modifierGroupAttributes = this.buildModifierGroupAttributes();
            const modifierAttributes = this.buildModifierAttributes();

            let attributesToAdd: CommonItemAttribute[];
            let attributesToRemove: CommonItemAttribute[];

            if (value === 'MODIFIER_GROUPING') {
                attributesToAdd = modifierGroupAttributes;
                attributesToRemove = modifierAttributes;
            } else if (value === 'MODIFIER') {
                attributesToAdd = modifierAttributes;
                attributesToRemove = modifierGroupAttributes;
            } else {
                attributesToAdd = [];
                attributesToRemove = [...modifierGroupAttributes, ...modifierAttributes];
            }

            const formChanged = this.updateClassAttributes(attributesToAdd, attributesToRemove);
            if (formChanged) {
                this.buildForm();
            }
        });
    }

    private updateClassAttributes(
        attributesToAdd: CommonItemAttribute[],
        attributesToRemove: CommonItemAttribute[]
    ): boolean {
        const tagsToRemove = new Set<string>(attributesToRemove.map(a => a.tag));
        const beforeLength = this.classAttributes.length;

        this.classAttributes = this.classAttributes.filter(attr => !tagsToRemove.has(attr.tag));

        let changed = false;
        attributesToAdd.forEach(attribute => {
            if (!this.hasAttribute(attribute.tag)) {
                this.classAttributes.push(attribute);
                changed = true;
            }
        });

        const afterLength = this.classAttributes.length;
        return changed || afterLength !== beforeLength;
    }

    private noWhitespaceValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value;
        if (value === null || value === undefined) {
            return null;
        }

        if (typeof value === 'string' && value.trim().length === 0) {
            return { whitespace: true };
        }

        return null;
    }

    private tagFormatValidator(control: FormControl): { [key: string]: any } | null {
        const value = control.value;
        if (value === null || value === undefined) {
            return null;
        }

        // Check if value only contains uppercase letters, numbers, and underscores
        if (typeof value === 'string') {
            const tagPattern = /^[A-Z0-9_]+$/;
            if (!tagPattern.test(value)) {
                return { tagFormat: true };
            }
        }

        return null;
    }
}
